import { Logo } from '@developerz/ui';
import { createSignal, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { apiFetch } from '../lib/api';
import { signOut } from '../lib/sign-out';
import type { ConsentReread } from './ConsentGate';
import { Button } from './ui/Button';

// The SEPARATE cross-border consent (#5881, PIPL Art. 38-39). <App/> renders it
// after the ToS wall and the region question, for a session `/v1/me` reports as
// `cross_border_consent.missing` — one the API has already capped to
// `read:account` (`sessionScopes`). A person who declared mainland China reaches
// it once, again after a notice version bump, and again after withdrawing.
//
// What makes it a SEPARATE consent, each a requirement rather than a style:
//   - its own screen, never a second box beside the ToS one;
//   - the notice states the recipients, the purpose and how to exercise rights
//     (`consent.cross_border_notice`, the zh legal draft's privacy policy § 5);
//   - the box starts UNTICKED and the button names the act ("Agree and continue");
//   - a way to say no: "I do not agree" shows what declining means and how to ask
//     for deletion, then offers the one sign-out (`lib/sign-out.ts`).
//
// Granting POSTs `/v1/me/consents` with the notice version it showed, then asks
// the caller to re-read `/v1/me`, exactly as <ConsentGate/> does.

export function CrossBorderGate(props: {
  /** `cross_border_consent.current_version` from `/v1/me` — echoed so the grant records what was shown. */
  version: string;
  token: string | null;
  /** Called after a successful grant: re-read `/v1/me`, answer whether it cleared. */
  onAccepted: () => Promise<ConsentReread>;
}): JSX.Element {
  const [checked, setChecked] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [declined, setDeclined] = createSignal(false);
  // Half-failure enum: which side of a save failed. The locale keys these map
  // to are `consent.cross_border_error` and `consent.cross_border_reload_error`
  // — a third state (`RegionGate`'s `'save'`) is the same shape under a
  // different name, so we keep the literal as the cue and let the JSX below
  // pick the matching key.
  const [failure, setFailure] = createSignal<'accept' | 'reload' | null>(null);

  const accept = async (): Promise<void> => {
    if (!checked() || busy()) return;
    setBusy(true);
    setFailure(null);
    try {
      await apiFetch('/v1/me/consents', props.token, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'cross_border_cn',
          decision: 'grant',
          version: props.version,
        }),
      });
    } catch {
      setFailure('accept');
      setBusy(false);
      return;
    }
    // A re-read that THREW did not clear the gate either, so it lands on the
    // same verdict as one answering `still_walled`. Unguarded, the rejection
    // escaped this handler: `busy` never reset and no failure ever painted, so
    // the screen sat on "Saving…" for good — no verdict on a consent the POST
    // above had already recorded, and no way back but a reload.
    let reread: ConsentReread;
    try {
      reread = await props.onAccepted();
    } catch {
      reread = 'still_walled';
    }
    if (reread === 'still_walled') {
      setFailure('reload');
      setBusy(false);
    }
  };

  return (
    <main class="login">
      <div class="card login__card">
        <header class="card__header login__header">
          <span class="login__logo" aria-hidden="true">
            <Logo variant="icon" height={36} />
          </span>
          <h1 class="card__title login__title">
            {declined()
              ? t('consent.cross_border_declined_title')
              : t('consent.cross_border_title')}
          </h1>
        </header>
        <div class="card__content login__content">
          <Switch>
            <Match when={declined()}>
              <p class="card__desc">{t('consent.cross_border_declined_body')}</p>
              <Button variant="outline" onClick={() => void signOut()}>
                {t('session.sign_out')}
              </Button>
              <Button variant="ghost" onClick={() => setDeclined(false)}>
                {t('consent.cross_border_back')}
              </Button>
            </Match>
            <Match when={!declined()}>
              <p class="card__desc">{t('consent.cross_border_notice')}</p>
              <Show when={failure()}>
                {(why) => (
                  <div class="alert alert--destructive" role="alert">
                    <p class="alert__title">
                      {why() === 'accept'
                        ? t('consent.cross_border_error')
                        : t('consent.cross_border_reload_error')}
                    </p>
                  </div>
                )}
              </Show>
              <label class="login__check">
                <input
                  type="checkbox"
                  checked={checked()}
                  onChange={(e) => setChecked(e.currentTarget.checked)}
                />
                {t('consent.cross_border_agree')}
              </label>
              <Button disabled={!checked() || busy()} onClick={accept}>
                {busy() ? t('consent.cross_border_saving') : t('consent.cross_border_cta')}
              </Button>
              <Button variant="outline" disabled={busy()} onClick={() => setDeclined(true)}>
                {t('consent.cross_border_decline')}
              </Button>
            </Match>
          </Switch>
        </div>
      </div>
    </main>
  );
}
